import { cmpLongDateFormatter } from "../../common/dashboard/formatters";

export type MockInvoiceType =
  | "membership"
  | "eventTicket"
  | "eventSponsorship"
  | "custom";

export type MockInvoiceSourceKind =
  | "standalone"
  | "member"
  | "eventRegistration"
  | "eventSponsorship";

export type MockInvoiceStatus =
  | "draft"
  | "unpaid"
  | "paid"
  | "void"
  | "waived"
  | "refunded"
  | "failed"
  | "abandoned"
  | "expired"
  | "actionRequired";

export type MockInvoiceLineItem = {
  id: string;
  code?: string;
  catalogOptionId?: string;
  accountNumber?: string;
  description: string;
  quantity: number;
  unitPriceInCents: number;
  taxRate: number;
};

export type MockInvoiceEmailRouting = {
  intendedRecipientEmail: string;
  actualRecipientEmail?: string;
  recipientOverrideUsed?: boolean;
  routingMode: "NOT_SENT" | "TEST_INBOX_OVERRIDE" | "LIVE_RECIPIENTS";
  lastSentAt?: string;
  state: "notSent" | "sent" | "failed" | "skipped";
};

export type MockInvoiceHistoryEvent = {
  id: string;
  label: string;
  occurredAt: string;
  actor: string;
  detail: string;
};

export type MockInvoice = {
  id: string;
  invoiceNumber: string;
  isCmpInvoiceModuleTestData?: boolean;
  type: MockInvoiceType;
  sourceKind: MockInvoiceSourceKind;
  sourceLabel: string;
  customerName: string;
  billingContactName: string;
  billingEmail: string;
  billingPhone: string;
  billingAddress: string;
  issueDate: string;
  dueDate?: string;
  daysPastDue?: number | null;
  paidDate?: string;
  status: MockInvoiceStatus;
  paymentMethodLabel: string;
  requestedPaymentMethodLabel: string;
  lineItems: MockInvoiceLineItem[];
  notes: string;
  publicMemo: string;
  emailRouting: MockInvoiceEmailRouting;
  history: MockInvoiceHistoryEvent[];
  replacementInvoiceId?: string;
  replacesInvoiceId?: string;
  providerMode?: "Stripe test" | "Manual";
};

export type MockInvoiceSourcePreset = {
  id: MockInvoiceSourceKind;
  label: string;
  helper: string;
  invoiceType: MockInvoiceType;
  sourceLabel: string;
  customerName: string;
  billingContactName: string;
  billingEmail: string;
  billingPhone: string;
  billingAddress: string;
  requestedPaymentMethodLabel: string;
  lineItems: MockInvoiceLineItem[];
};

export type OverdueBucket = "all" | "current" | "60" | "90" | "120";

const dayInMs = 24 * 60 * 60 * 1000;

export const getIsoDateDaysFromToday = (offsetDays: number) => {
  const date = new Date();
  date.setHours(12, 0, 0, 0);
  date.setTime(date.getTime() + offsetDays * dayInMs);
  return date.toISOString().slice(0, 10);
};

export const todayIso = () => getIsoDateDaysFromToday(0);

export const formatCurrencyFromCents = (amountInCents: number) =>
  new Intl.NumberFormat("en-CA", {
    currency: "CAD",
    style: "currency",
  }).format(amountInCents / 100);

// SH-004 date consistency: delegate to the shared long-form formatter so the
// UpdateInvoice sidebar preview matches manage tables / detail pages
// ("January 01, 2026") instead of the old en-US short form ("Jul 1, 2026").
// Inputs are always YYYY-MM-DD date-input strings (see utcMilliToDateInput);
// the noon local time keeps the calendar day stable regardless of timezone.
export const formatDateForDisplay = (date?: string) => {
  if (!date) {
    return "-";
  }

  return cmpLongDateFormatter.format(new Date(`${date}T12:00:00`));
};

export const getInvoiceSubtotalInCents = (invoice: Pick<MockInvoice, "lineItems">) =>
  invoice.lineItems.reduce(
    (total, item) => total + item.quantity * item.unitPriceInCents,
    0
  );

export const getInvoiceTaxInCents = (invoice: Pick<MockInvoice, "lineItems">) =>
  invoice.lineItems.reduce(
    (total, item) =>
      total +
      Math.round(item.quantity * item.unitPriceInCents * (item.taxRate / 100)),
    0
  );

export const getInvoiceTotalInCents = (invoice: Pick<MockInvoice, "lineItems">) =>
  getInvoiceSubtotalInCents(invoice) + getInvoiceTaxInCents(invoice);

type InvoiceDueState = Pick<MockInvoice, "dueDate" | "status" | "daysPastDue">;

export const getDaysPastDue = (invoice: InvoiceDueState) => {
  if (typeof invoice.daysPastDue === "number") {
    return Math.max(0, invoice.daysPastDue);
  }

  if (invoice.status !== "unpaid" || !invoice.dueDate) {
    return 0;
  }

  const dueDate = new Date(`${invoice.dueDate}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return Math.max(0, Math.floor((today.getTime() - dueDate.getTime()) / dayInMs));
};

export const isInvoiceOverdue = (invoice: InvoiceDueState) =>
  getDaysPastDue(invoice) > 0;

export const getOverdueBucket = (invoice: InvoiceDueState): OverdueBucket => {
  const daysPastDue = getDaysPastDue(invoice);

  if (daysPastDue >= 120) {
    return "120";
  }

  if (daysPastDue >= 90) {
    return "90";
  }

  if (daysPastDue >= 60) {
    return "60";
  }

  return daysPastDue > 0 ? "current" : "all";
};

export const getInvoiceTypeLabel = (type: MockInvoiceType) => {
  switch (type) {
    case "membership":
      return "Membership";
    case "eventTicket":
      return "Event Ticket";
    case "eventSponsorship":
      return "Event Sponsorship";
    case "custom":
      return "Custom";
  }
};

export const getInvoiceStatusLabel = (invoice: MockInvoice) => {
  if (isInvoiceOverdue(invoice)) {
    return `${getDaysPastDue(invoice)} days overdue`;
  }

  switch (invoice.status) {
    case "draft":
      return "Draft";
    case "unpaid":
      return "Unpaid";
    case "paid":
      return "Paid";
    case "void":
      return "Void";
    case "waived":
      return "Waived";
    case "refunded":
      return "Refunded";
    case "failed":
      return "Failed";
    case "abandoned":
      return "Abandoned";
    case "expired":
      return "Expired";
    case "actionRequired":
      return "Action required";
  }
};

export const canEditInvoice = (invoice: MockInvoice) => invoice.status === "unpaid";

export const canRefundInvoice = (invoice: MockInvoice) => invoice.status === "paid";

export const invoiceSourcePresets: MockInvoiceSourcePreset[] = [
  {
    id: "standalone",
    label: "Standalone Custom",
    helper: "Create a completely custom invoice that is not tied to another CMP record.",
    invoiceType: "custom",
    sourceLabel: "Custom invoice",
    customerName: "",
    billingContactName: "",
    billingEmail: "",
    billingPhone: "",
    billingAddress: "",
    requestedPaymentMethodLabel: "Invoice Me",
    lineItems: [
      {
        id: "custom-1",
        description: "Custom invoice item",
        quantity: 1,
        unitPriceInCents: 0,
        taxRate: 0,
      },
    ],
  },
  {
    id: "member",
    label: "Member",
    helper: "Create an invoice tied to a member record or membership renewal.",
    invoiceType: "membership",
    sourceLabel: "Member invoice",
    customerName: "",
    billingContactName: "",
    billingEmail: "",
    billingPhone: "",
    billingAddress: "",
    requestedPaymentMethodLabel: "Invoice Me",
    lineItems: [
      {
        id: "member-1",
        description: "Membership invoice item",
        quantity: 1,
        unitPriceInCents: 0,
        taxRate: 0,
      },
    ],
  },
  {
    id: "eventRegistration",
    label: "Event Ticket",
    helper: "Create an invoice tied to an event registration or attendee ticket.",
    invoiceType: "eventTicket",
    sourceLabel: "Event registration invoice",
    customerName: "",
    billingContactName: "",
    billingEmail: "",
    billingPhone: "",
    billingAddress: "",
    requestedPaymentMethodLabel: "Credit card checkout",
    lineItems: [
      {
        id: "ticket-1",
        description: "Event ticket invoice item",
        quantity: 1,
        unitPriceInCents: 0,
        taxRate: 0,
      },
    ],
  },
  {
    id: "eventSponsorship",
    label: "Event Sponsorship",
    helper: "Create an invoice tied to an approved event sponsorship request.",
    invoiceType: "eventSponsorship",
    sourceLabel: "Event sponsorship invoice",
    customerName: "",
    billingContactName: "",
    billingEmail: "",
    billingPhone: "",
    billingAddress: "",
    requestedPaymentMethodLabel: "Bank transfer",
    lineItems: [
      {
        id: "sponsor-1",
        description: "Event sponsorship invoice item",
        quantity: 1,
        unitPriceInCents: 0,
        taxRate: 0,
      },
    ],
  },
];
